import React, { useCallback, useMemo } from 'react';
import { Box, CircularProgress, Switch } from '@mui/material';
import type { RenderColumnDataProps } from 'c-pagination';
import type { PopDisplays_LogImportSource } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { AutoGrid } from 'c-components';
import { useUpdateEntityData } from 'c-data';
import { NetworkRequestState } from 'c-data-layer';
import { IfHasAllPermissions } from 'c-auth-module/Components';
import { useCommonTranslation } from 'c-translation';

type Props = RenderColumnDataProps<PopDisplays_LogImportSource>;

const updatePerms = [PermissionName.Pop_displaysLog_import_sourceUpdate];

const LogFileSourceStatusCell: React.FC<Props> = ({ entity, defaultContent }) => {
    const { update, getUpdatingById } = useUpdateEntityData<PopDisplays_LogImportSource>(
        'PopDisplays_LogImportSource',
    );
    const loading = useMemo(
        () => getUpdatingById(entity?.id).state === NetworkRequestState.InProgress,
        [entity?.id, getUpdatingById],
    );

    const t = useCommonTranslation();

    const onChange = useCallback(() => {
        if (entity) {
            update(entity.id, {
                id: entity.id,
                active: !entity.active,
            } as PopDisplays_LogImportSource);
        }
    }, [update, entity]);

    return (
        <IfHasAllPermissions
            otherwise={
                <Box width="100%" display="flex" justifyContent="center">
                    {defaultContent}
                </Box>
            }
            permissions={updatePerms}
        >
            <AutoGrid columnGap={2}>
                <Switch
                    checked={entity.active}
                    onChange={onChange}
                    size="small"
                    inputProps={{
                        'aria-label': t(
                            'Modules.PopDisplays.LogImportSource.table.activeToggleLabel',
                            {
                                name: entity.name,
                            },
                        ),
                    }}
                />
                <CircularProgress
                    size={15}
                    sx={{ visibility: loading ? 'visible' : 'hidden' }}
                    aria-label={t('Modules.PopDisplays.LogImportSource.table.updatingLabel', {
                        name: entity.name,
                    })}
                />
            </AutoGrid>{' '}
        </IfHasAllPermissions>
    );
};

export default LogFileSourceStatusCell;
